import GroupLayer from '@arcgis/core/layers/GroupLayer';
import FeatureLayer, { FeatureLayerProperties } from '@arcgis/core/layers/FeatureLayer';

/**
   * The base ArcGIS ESRI proxy service URL — all GIS map server URLs
   * are built from this base.
   *
   * Local-dev override for the entire `esriproxy` passthrough (ari-esri-proxy):
   * set `VITE_ESRI_PROXY_USE_LOCALHOST=true` and `VITE_ESRI_PROXY_LOCALHOST_URL` in
   * `.env.local` to point all FeatureLayer URLs at a local esri-proxy instance.
   */
// Local-dev override for the entire `esriproxy` passthrough (ari-esri-proxy),
// so every FeatureLayer built from `BaseGisService.url` in this file can be
// pointed at a locally-running `ari-esri-proxy` Function host instead of the
// deployed API Manager route — flip `VITE_ESRI_PROXY_USE_LOCALHOST=true` and
// set `VITE_ESRI_PROXY_LOCALHOST_URL` (defaults to the port ari-esri-proxy's
// own launchSettings.json runs on, `http://localhost:7081/api`) in
// apps/prism2/.env.local. Never set these in .env.production.
const useLocalEsriProxy = import.meta.env.VITE_ESRI_PROXY_USE_LOCALHOST === 'true';
const localEsriProxyBase = (import.meta.env.VITE_ESRI_PROXY_LOCALHOST_URL as string | undefined) || 'http://localhost:7081/api';
/**
 * The base ArcGIS ESRI proxy service URL configuration — all GIS map server URLs are built from this base. Uses a local development override when VITE_ESRI_PROXY_USE_LOCALHOST is set to 'true'.
 *
 * In local development, sets the esriproxy passthrough (ari-esri-proxy) to point at a locally-running instance instead of the deployed API Manager route by checking the VITE_ESRI_PROXY_USE_LOCALHOST and VITE_ESRI_PROXY_LOCALHOST_URL environment variables. Never set these in .env.production.
 *
 * @returns An object with a `url` property containing the base GIS service URL
 */
export const BaseGisService = {
  url: useLocalEsriProxy
    ? `${localEsriProxyBase}/esriproxy/rest/services`
    : 'https://ari-api-manager.azure-api.net/ari-esri-proxy/esriproxy/rest/services'
};

/**
 * Map server configuration for the AIM OpportunityMap — the base
 * `MapServer` used by the acquisition view.
 */
export const AimMapViewAcquisitionServer = {
  url: `${BaseGisService.url}/AIM/OpportunityMap/MapServer`
};
/**
 * Feature server configuration for the AIM Opportunity Edit Map —
 * the `FeatureServer` used for editing acquisition features.
 */
export const EditAimMapViewServer = {
  url: `${BaseGisService.url}/AIM/OpportunityEditMap/FeatureServer`
};
/**
 * Map server configuration for the AIM Opportunity Edit Map (MapServer) —
 * used for reading acquisition data before edits.
 */
export const AimPAMapViewServer = {
  url: `${BaseGisService.url}/AIM/OpportunityEditMap/MapServer`
};

/**
 * Map server configuration for the Potential Acquisition Layers — the
 * `MapServer/3` layer exposing GIS data on potential acquisition fields
 * (leased land with acquisition opportunity).
 */
export const PotentialAcquisitionFieldsMapViewServer = {
  url: `${BaseGisService.url}/Leased/PotentialAcquisitionLayers/MapServer/3`
};

/**
 * Map server configuration for the AGNW `/Crop_Symbology_Setup_2022`
 * service — the source of agricultural crop symbology/layer
 * information.
 */
export const TerraFieldMapViewServer = {
  url: `${BaseGisService.url}/AGNW/Crop_Symbology_Setup_2022/MapServer`
};

/**
 * Map server configuration for the `FarManagmentLayers` service —
 * the source of lease management and farm management GIS layers.
 */
export const LeaseManagementMapViewServer = {
  url: `${BaseGisService.url}/Leased/FarmManagmentLayers/MapServer` // layer 4
};

/**
 * The ArcGIS proxy service base URL (a plain string alias for
 * `BaseGisService.url` convenience).
 */
export const azureProxyEsriService = `${BaseGisService.url}`;




/**
 * Map server configuration for property boundaries — the Jarvis IGP layer 14
 * MapServer.
 */
export const PropertyBoundariesMapServer = {
  url: `${BaseGisService.url}/Jarvis/IGP_GIS_Layers/MapServer/14`
};

/**
 * Map server configuration for deeded property layers — the Jarvis IGP layer index 2
 * MapServer.
 */
export const DeededMapServer = {
  url: `${BaseGisService.url}/Jarvis/IGP_GIS_Layers/MapServer/2/`
};

/**
 * Map server configuration for mineral rights GIS data — the `IGP_GIS_Layers/MapServer/8/` layer.
 */
export const MineralRightsMapServer = {
  url: `${BaseGisService.url}/Jarvis/IGP_GIS_Layers/MapServer/8/`
};

/**
 * Map server configuration for water sources GIS data — the `IGP_GIS_Layers/MapServer/9/` layer.
 */
export const WaterSourcesMapServer = {
  layerName: 'Water Sources',
  url: `${BaseGisService.url}/Jarvis/IGP_GIS_Layers/MapServer/9/`
};

/**
 * Map server configuration for water transport GIS data — the `IGP_GIS_Layers/MapServer/10/` layer.
 */
export const WaterTransportMapServer = {
  layerName: 'Water Transport',
  url: `${BaseGisService.url}/Jarvis/IGP_GIS_Layers/MapServer/10/`
};

/**
 * Map server configuration for water assets GIS data — the `IGP_GIS_Layers/MapServer/11/` layer.
 */
export const WaterAssetsMapServer = {
  layerName: 'Water Assets',
  url: `${BaseGisService.url}/Jarvis/IGP_GIS_Layers/MapServer/11/`
};

/**
 * Map server configuration for GIS point-of-diversion water resource
 * data — the `WaterAndNaturalResourceLayers/MapServer/6` layer tracking
 * water diversion points.
 */
export const PointsOfDiversionMapServer = {
  layerName: 'Point of Diversion',
  url: `${BaseGisService.url}/Leased/WaterAndNaturalResourceLayers/MapServer/6/`
};

/**
 * Map server configuration for the `Lease_Contract_Management_Fields`
 * service — provides GIS data for lease contract management field
 * boundaries (layer index 2).
 */
export const LeaseMapServer = {
  // Lease_Contract_Management_Fields_Boundaries
  layerName: 'Lease',
  url: `${BaseGisService.url}/Leased/Lease_Contract_Management_Fields/MapServer/2`
};

/**
 * Map server configuration for lease field GIS data — the
 * `Lease_Contract_Management_Fields/MapServer/3` layer exposing lease
 * field polygon boundaries.
 */
export const LeaseFieldsMapServer = {
  layerName: 'Lease Fields',
  url: `${BaseGisService.url}/Leased/Lease_Contract_Management_Fields/MapServer/3/`
};

/**
 * Map server configuration for solar lease GIS data — the
 * `IGP_GIS_Layers/MapServer/6` layer providing solar lease
 * boundary/parcel information.
 */
export const SolarLeaseMapServer = {
  layerName: 'Lease',
  url: `${BaseGisService.url}/Jarvis/IGP_GIS_Layers/MapServer/6/`
};

/**
 * Map server configuration for GIS easement data — the
 * `IGP_GIS_Layers/MapServer/0` layer providing easement parcel
 * boundaries.
 */
export const EasementsMapServer = {
  layerName: 'Easements',
  url: `${BaseGisService.url}/Jarvis/IGP_GIS_Layers/MapServer/0/`
};
/**
 * Map server configuration for the AIM Opportunity Map — base layer serving
 * US states, counties, countries, and acquisition-level GIS features.
 */
export const AIMMapServer = {
  USStates: {
    url: `${BaseGisService.url}/AIM/OpportunityMap/MapServer/6/`,
    censusURL: 'https://services.arcgis.com/P3ePLMYs2RVChkJx/arcgis/rest/services/USA_Census_States/FeatureServer/0'
  },
  USCounties: {
    url: `${BaseGisService.url}/AIM/OpportunityMap/MapServer/5/`
  },
  Countries: {
    // url: 'https://services.arcgis.com/P3ePLMYs2RVChkJx/arcgis/rest/services/World_Countries/FeatureServer/0'
    // url: 'https://services.arcgis.com/P3ePLMYs2RVChkJx/arcgis/rest/services/World_Countries_(Generalized)/FeatureServer/0'
    // url: 'https://services6.arcgis.com/kOvusPqAUjVWKF9D/arcgis/rest/services/National_boundaries_1km/FeatureServer/0'
    url: `${BaseGisService.url}/AIM/OpportunityMap/MapServer/7` // ISO id: FIPS_CNTRY
  },
  Acquisitions: {
    /*  attributes:
        OBJECTID ( type: esriFieldTypeOID, alias: OBJECTID, editable: false, nullable: false, defaultValue: null, modelName: OBJECTID )
        NAME ( type: esriFieldTypeString, alias: Name, editable: true, nullable: true, length: 30, defaultValue: null, modelName: NAME )
        OP_TYPE ( type: esriFieldTypeString, alias: Operation Type, editable: true, nullable: true, length: 20, defaultValue: null, modelName: OP_TYPE )
        STATUS ( type: esriFieldTypeString, alias: Status, editable: true, nullable: true, length: 20, defaultValue: null, modelName: STATUS , Coded Values: [Active: Property leased or direct operated] , [Inactive: Property sold] , [Sale Pending: Property for sale] , ...3 more... )
        ACRES ( type: esriFieldTypeDouble, alias: GIS Acres, editable: true, nullable: true, defaultValue: null, modelName: ACRES )
        POLITICAL_DIVISION ( type: esriFieldTypeString, alias: Political Division, editable: true, nullable: true, length: 20, defaultValue: null, modelName: STATE )
        ABBV_NAME ( type: esriFieldTypeString, alias: ABBV_NAME, editable: true, nullable: true, length: 6, defaultValue: null, modelName: ABBV_NAME )
        FARMNUM ( type: esriFieldTypeString, alias: FARMNUM, editable: true, nullable: true, length: 50, defaultValue: null, modelName: FARMNUM )
        GlobalID ( type: esriFieldTypeGlobalID, alias: GlobalID, editable: false, nullable: false, length: 38, defaultValue: null, modelName: GlobalID )
        CHURCH_Prop_ID ( type: esriFieldTypeString, alias: CHURCH_Prop_ID, editable: true, nullable: true, length: 50, defaultValue: null, modelName: CHURCH_Prop_ID )
        Notes ( type: esriFieldTypeString, alias: Notes, editable: true, nullable: true, length: 500, defaultValue: null, modelName: Notes )
        DataSource ( type: esriFieldTypeString, alias: DataSource, editable: true, nullable: true, length: 50, defaultValue: null, modelName: DataSource )
        CreationDate ( type: esriFieldTypeDate, alias: CreationDate, editable: true, nullable: true, length: 8, defaultValue: null, modelName: CreationDate )
        ModifiedDate ( type: esriFieldTypeDate, alias: ModifiedDate, editable: true, nullable: true, length: 8, defaultValue: null, modelName: ModifiedDate )
        CompanyUser ( type: esriFieldTypeString, alias: CompanyUser, editable: true, nullable: true, length: 50, defaultValue: null, modelName: CompanyUser )
        ReferenceLink ( type: esriFieldTypeString, alias: ReferenceLink, editable: true, nullable: true, length: 500, defaultValue: null, modelName: ReferenceLink )
        Source_Types ( type: esriFieldTypeString, alias: Source_Types, editable: true, nullable: true, length: 50, defaultValue: null, modelName: Source_Types , Coded Values: [Legal Description: Legal Description] , [Employee Knowledge: Employee Knowledge] , [Physical Asset: Physical Asset] , ...6 more... )
        Source_Types_Other ( type: esriFieldTypeString, alias: Source_Types_Other, editable: true, nullable: true, length: 50, defaultValue: null, modelName: Source_Types_Other )
        Geometry_Creation ( type: esriFieldTypeString, alias: Geometry_Creation, editable: true, nullable: true, length: 50, defaultValue: null, modelName: Geometry_Creation , Coded Values: [RTK: RTK] , [Corrected_GPS: Corrected GPS] , [COGO_Tool: COGO Tool] , ...9 more... )
        Geometry_Creation_Other ( type: esriFieldTypeString, alias: Geometry_Creation_Other, editable: true, nullable: true, length: 50, defaultValue: null, modelName: Geometry_Creation_Other )
        Data_Quality ( type: esriFieldTypeString, alias: Data_Quality, editable: true, nullable: true, length: 50, defaultValue: null, modelName: Data_Quality , Coded Values: [Excellent: 1 - Excellent] , [Good: 2 - Good] , [Moderate: 3 - Moderate] , ...2 more... )
        Data_Quality_Other ( type: esriFieldTypeString, alias: Data_Quality_Other, editable: true, nullable: true, length: 50, defaultValue: null, modelName: Data_Quality_Other )
        Document_Locations ( type: esriFieldTypeString, alias: Document_Locations, editable: true, nullable: true, length: 50, defaultValue: null, modelName: Document_Locations , Coded Values: [LIS: LIS] , [FRI Electronic Repository: FRI Electronic Repository] , [FRI Document Files: FRI Document Files] , ...6 more... )
        Document_Locations_Other ( type: esriFieldTypeString, alias: Document_Locations_Other, editable: true, nullable: true, length: 50, defaultValue: null, modelName: Document_Locations_Other )
        Flags ( type: esriFieldTypeString, alias: Flags, editable: true, nullable: true, length: 50, defaultValue: null, modelName: Flags , Coded Values: [Deed_Conflict: Deed Conflict] , [Doc_Not_Scanned: Document not Scanned] , [Border_Probs: Border Problems] , ...2 more... )
        Flags_Other ( type: esriFieldTypeString, alias: Flags_Other, editable: true, nullable: true, length: 50, defaultValue: null, modelName: Flags_Other )
        Shape__Area ( type: esriFieldTypeDouble, alias: SHAPE.STArea(), editable: false, nullable: true, defaultValue: null, modelName: SHAPE.STArea() )
        Shape__Length ( type: esriFieldTypeDouble, alias: SHAPE.STLength(), editable: false, nullable: true, defaultValue: null, modelName: SHAPE.STLength() )
        created_user ( type: esriFieldTypeString, alias: created_user, editable: false, nullable: true, length: 255, defaultValue: null, modelName: created_user )
        created_date ( type: esriFieldTypeDate, alias: created_date, editable: false, nullable: true, length: 8, defaultValue: null, modelName: created_date )
        last_edited_user ( type: esriFieldTypeString, alias: last_edited_user, editable: false, nullable: true, length: 255, defaultValue: null, modelName: last_edited_user )
        last_edited_date ( type: esriFieldTypeDate, alias: last_edited_date, editable: false, nullable: true, length: 8, defaultValue: null, modelName: last_edited_date )
        JARVISID ( type: esriFieldTypeString, alias: JARVISID, editable: true, nullable: true, length: 150, defaultValue: null, modelName: JARVISID )
        HandleID ( type: esriFieldTypeString, alias: HandleID, editable: true, nullable: true, length: 150, defaultValue: null, modelName: HandleID )
        Division ( type: esriFieldTypeString, alias: Division, editable: true, nullable: true, length: 50, defaultValue: null, modelName: Division , Coded Values: [Cattle: Cattle] , [Lease: Lease] , [Citrus: Citrus] , ...3 more... )
        NCCPI ( type: esriFieldTypeString, alias: NCCPI, editable: true, nullable: true, length: 250, defaultValue: null, modelName: temp_ChurchID )
        OpportunityID ( type: esriFieldTypeSmallInteger, alias: OpportunityID, editable: true, nullable: true, defaultValue: null, modelName: OpportunityID )
     */
    url: `${BaseGisService.url}/AIM/OpportunityEditMap/MapServer/0`,
    outFields: ['*'],
    definitionExpression: '1=1',
    effect: [
      // { scale: 10000_000, value: 'opacity(0)' },

      { scale: 5000000, value: 'bloom(2, 0.5px ,0) opacity(10%)' },
      { scale: 1_500_000, value: 'bloom(0, 0px ,0) opacity(100%)' }
    ],
    labelingInfo: [
      {
        //pins
        maxScale: 1_500_000,
        labelExpressionInfo: { expression: '$feature.NAME' },
        symbol: {
          type: 'text', // autocasts as new TextSymbol()
          color: '#7A003C',
          text: '\ue61d', // esri-icon-map-pin
          font: {
            // autocasts as new Font()
            size: 36,
            family: 'CalciteWebCoreIcons' // Esri Icon Font
          }
        }
      },
      {
        maxScale: 1_249_200,
        minScale: 1_500_000,
        labelExpressionInfo: { expression: '$feature.NAME' },
        symbol: { type: 'text', color: 'white', haloColor: 'black', haloSize: 2, yoffset: 10 }
      },
      {
        maxScale: 0,
        minScale: 1_249_200,
        labelExpressionInfo: {
          expression:
            'Concatenate($feature.NAME, TextFormatting.NewLine, Floor($feature.ACRES*100)/100, " AC");'
        }
      }
    ]
    // featureReduction: {
    //   type: 'cluster',
    //   clusterRadius: '80px',
    //   maxScale: 1_749_200,
    //   clusterMaxSize: 20,
    //   clusterMinSize: 20,
    //   labelingInfo: [
    //     {
    //       deconflictionStrategy: 'none',
    //       labelExpressionInfo: {
    //         expression: "Text($feature.cluster_count, '#,###')"
    //       },
    //       symbol: {
    //         type: 'text',
    //         color: '#004a5d',
    //         font: {
    //           weight: 'bold',
    //           family: 'Noto Sans',
    //           size: '12px'
    //         }
    //       },
    //       labelPlacement: 'center-center'
    //     }
    //   ]
    // }
  } as FeatureLayerProperties
};

/**
 * Row crop map server configurations — AGNW (Canyon) and CFF crop symbology layers.
 */
export const RowCropLayers = {
  /**
   * Division ( type: esriFieldTypeString, alias: Division, length: 10 )
   * FieldName ( type: esriFieldTypeString, alias: FieldName, length: 11 )
   * CropYear ( type: esriFieldTypeSmallInteger, alias: CropYear )
   * Customer ( type: esriFieldTypeString, alias: Customer, length: 30 )
   * DirectManaged ( type: esriFieldTypeInteger, alias: DirectManaged )
   * EnterpriseAbbr ( type: esriFieldTypeString, alias: EnterpriseAbbr, length: 10 )
   * EnterpriseDescription ( type: esriFieldTypeString, alias: EnterpriseDescription, length: 100 )
   * PlantedAcres ( type: esriFieldTypeDouble, alias: PlantedAcres )
   * PlantingRate ( type: esriFieldTypeDouble, alias: PlantingRate )
   * PlantingUOM ( type: esriFieldTypeString, alias: PlantingUOM, length: 50 )
   * RotationStatus ( type: esriFieldTypeString, alias: RotationStatus, length: 1 )
   * VarietyAbbr ( type: esriFieldTypeString, alias: VarietyAbbr, length: 4 )
   * VarietyDescription ( type: esriFieldTypeString, alias: VarietyDescription, length: 100 )
   * Yield ( type: esriFieldTypeDouble, alias: Yield )
   * YieldUOM ( type: esriFieldTypeString, alias: YieldUOM, length: 50 )
   * GeoAcres ( type: esriFieldTypeDouble, alias: GeoAcres )
   * UsableAcres ( type: esriFieldTypeDouble, alias: UsableAcres )
   * Field ( type: esriFieldTypeString, alias: Field, length: 50 )
   * CropYearCycle ( type: esriFieldTypeSmallInteger, alias: CropYearCycle )
   * AreaUnitID ( type: esriFieldTypeString, alias: AreaUnitID, length: 61 )
   * SDE_STATE_ID ( type: esriFieldTypeDouble, alias: SDE_STATE_ID )
   * GlobalID ( type: esriFieldTypeGUID, alias: GlobalID, length: 38 )
   * Shape ( type: esriFieldTypeGeometry, alias: Shape )
   * OBJECTID ( type: esriFieldTypeInteger, alias: OBJECTID )
   * Area ( type: esriFieldTypeInteger, alias: Area )
   * Unit ( type: esriFieldTypeInteger, alias: Unit )
   * CycleBeginDate ( type: esriFieldTypeDate, alias: CycleBeginDate, length: 8 )
   * CycleEndDate ( type: esriFieldTypeDate, alias: CycleEndDate, length: 8 )
   * Description ( type: esriFieldTypeInteger, alias: Description )
   * EmergeDate ( type: esriFieldTypeDate, alias: EmergeDate, length: 8 )
   * HarvestDate ( type: esriFieldTypeDate, alias: HarvestDate, length: 8 )
   * PlantDate ( type: esriFieldTypeDate, alias: PlantDate, length: 8 )
   * ESRI_OID ( type: esriFieldTypeOID, alias: ESRI_OID )
   */
  AGNW: {
    // url: 'http://localhost:7081/api/esriproxy/rest/services/AGNW/Crop_Symbology_Setup_2022/MapServer/1'
    url: `${BaseGisService.url}/AGNW/Crop_Symbology_Setup_2022/MapServer/1`
    // url: `${BaseGisService.url}/TERRA/TerraMobile_FieldCrop/MapServer/1`
  },
  // CFF: { url: 'http://localhost:7081/api/esriproxy/rest/services/CFF/CFF_Crops/MapServer/0' }
  CFF: { url: `${BaseGisService.url}/CFF/CFF_Crops/MapServer/0` }
};

/**
 * Permanent planting (orchard/vineyard) map server configurations across divisions —
 * SVF, DFC (walnut/alfalfa blocks), SHO (Sherman Orchards), and ARC (Chile).
 */
export const PermPlantingCropLayers = {
// OBJECTID ( type: esriFieldTypeOID, alias: OBJECTID )
// Block ( type: esriFieldTypeString, alias: Block, length: 255 )
// GIS_Acres ( type: esriFieldTypeDouble, alias: GIS Acres )
// Tree_Acres ( type: esriFieldTypeDouble, alias: Tree Acres )
// Crop ( type: esriFieldTypeString, alias: Crop, length: 255 )
// Status ( type: esriFieldTypeString, alias: Status, length: 255 )
// Ranch ( type: esriFieldTypeString, alias: Ranch, length: 255 )
// Foreman ( type: esriFieldTypeString, alias: Foreman, length: 255 )
// PCA ( type: esriFieldTypeString, alias: PCA, length: 255 )
// Varieties ( type: esriFieldTypeString, alias: Varieties, length: 255 )
// Varieties_Abbreviated ( type: esriFieldTypeString, alias: Varieties (Abbreviated), length: 255 )
// Rootstocks ( type: esriFieldTypeString, alias: Rootstocks, length: 255 )
// Planting_Year ( type: esriFieldTypeString, alias: Planting Year, length: 255 )
// Production_Year ( type: esriFieldTypeString, alias: Production Year, length: 255 )
// Purchase_Year ( type: esriFieldTypeString, alias: Purchase Year, length: 255 )
// Tree_Count ( type: esriFieldTypeInteger, alias: Tree Count )
// Row_Space_Feet ( type: esriFieldTypeDouble, alias: Row Space (Feet) )
// Tree_Space_Feet ( type: esriFieldTypeDouble, alias: Tree Space (Feet) )
// Irrigation_System_Type ( type: esriFieldTypeString, alias: Irrigation System Type, length: 255 )
// Nozzle_Type ( type: esriFieldTypeString, alias: Nozzle Type, length: 255 )
// Previous_Block_Name ( type: esriFieldTypeString, alias: Previous Block Name, length: 255 )
// Previous_Crop ( type: esriFieldTypeString, alias: Previous Crop, length: 255 )
// Previous_Crop_Years ( type: esriFieldTypeString, alias: Previous Crop Years, length: 255 )
// Future_Crop ( type: esriFieldTypeString, alias: Future Crop, length: 255 )
// Future_Crop_Plant_Year ( type: esriFieldTypeString, alias: Future Crop Plant Year, length: 255 )
// PLSS_Section ( type: esriFieldTypeString, alias: PLSS Section, length: 255 )
// PLSS_Township_Range ( type: esriFieldTypeString, alias: PLSS Township-Range, length: 255 )
// CHIPS_ID ( type: esriFieldTypeString, alias: CHIPS ID, length: 255 )
// TaxDescription ( type: esriFieldTypeString, alias: TaxDescription, length: 255 )
// MarketValueCode ( type: esriFieldTypeString, alias: MarketValueCode, length: 255 )
// Long_Description ( type: esriFieldTypeString, alias: Long Description, length: 255 )
// Lessee ( type: esriFieldTypeString, alias: Lessee, length: 255 )
// Lease_Expiration ( type: esriFieldTypeString, alias: Lease Expiration, length: 255 )
  SVF: { url: `${BaseGisService.url}/California/SVF_FeatureService/MapServer/2` },
  /** DFC Blocks - Crop Types layer (Status = "Walnut Bearing", "Almond Non-Bearing", etc.) */
  DFC: { url: `${BaseGisService.url}/California/DFC_FeatureService/MapServer/0` },
  SHO: { url: `${BaseGisService.url}/SHO/SHO_Blocks/MapServer/0` },
  /**
   * Chile (ARC) Equipos (Blocks) layer — Spanish field names:
   * Equipo=Block, Cultivo=Crop, Variedad=Variety, Hectáreas=Hectares,
   * Año_de_Plantación=Planting Year, EstadodeEquipo=Block Status
   */
  ARC: { url: `${BaseGisService.url}/Chile/2019-agroreservas-de-chile-map-general-map-viewer/MapServer/16` }
};

/**
 * SVF CHIPS VIEW — augments the SVF perm-planting blocks with yield columns
 * (GreenWt, EdibleWt, InedibleWt, GoodMeatWt, KernelWt, HarvestAcres,
 * USDALbsPerAcre) and a CropYear field, enabling year-over-year yield queries.
 *
 * Service: /SVF/SVF_CHIPS_VIEW/MapServer/0
 */
export const SVFChipsViewMapServer = {
  layerName: 'SVF CHIPS VIEW',
  url: `${BaseGisService.url}/SVF/SVF_CHIPS_VIEW/MapServer/0`
};

/**
 * Farm Unit layers — used to spatially query Area values for
 * cross-state properties that need state-scoped Terra crop filtering.
 *
 * Field naming differs per division:
 *  - CFF: `AreaArea` (smallint)
 *  - AGNW: `Area` (int)
 *  - TVF/ONT: single-state, no farm-unit layer needed
 */
export interface FarmUnitLayerConfig {
  url: string;
  /** The field name for the "Area" identifier */
  areaField: string;
}

/**
 * Farm unit layers — used to spatially query `Area` values for
 * cross-state properties that need state-scoped Terra crop filtering.
 */
export const FarmUnitLayers: Record<string, FarmUnitLayerConfig> = {
  CFF: {
    url: `${BaseGisService.url}/CFF/CFF_Farm_Units/MapServer/22`,
    areaField: 'AreaArea'
  },
  AGNW: {
    url: `${BaseGisService.url}/AGNW/AGNW_Farm_Units/MapServer/1`,
    areaField: 'Area'
  }
};

/**
 * Map server configurations for infrastructure and building GIS layers — building points and building footprints by farm/location.
 */
export const BuildingLayers = {
  BuildingPoints: {
    url: `${BaseGisService.url}/Buildings/Buildings/MapServer/0`
  },
  Footprints: {
    CFF: {
      url: `${BaseGisService.url}/CFF/CanyonFallsFarm/MapServer/19`
    },
    TVF: {
      url: `${BaseGisService.url}/TVF/TVF/MapServer/4`
    }
  }
};

/*
    Fields:
    OBJECTID ( type: esriFieldTypeOID, alias: OBJECTID )
    BusinessUnitNumber ( type: esriFieldTypeString, alias: BusinessUnitNumber, length: 50 )
    Operation ( type: esriFieldTypeString, alias: Operation, length: 50 )
    FarmNumber ( type: esriFieldTypeString, alias: FarmNumber, length: 50 )
    Unit ( type: esriFieldTypeString, alias: Unit, length: 50 )
    FieldID ( type: esriFieldTypeString, alias: FieldID, length: 50 )
    Year ( type: esriFieldTypeSmallInteger, alias: Year )
    GIS_Acres ( type: esriFieldTypeDouble, alias: GIS_Acres )
    MarketValueCode ( type: esriFieldTypeSmallInteger, alias: MarketValueCode )
    SHAPE ( type: esriFieldTypeGeometry, alias: SHAPE )
    created_user ( type: esriFieldTypeString, alias: created_user, length: 255 )
    created_date ( type: esriFieldTypeDate, alias: created_date, length: 8 )
    last_edited_user ( type: esriFieldTypeString, alias: last_edited_user, length: 255 )
    last_edited_date ( type: esriFieldTypeDate, alias: last_edited_date, length: 8 )
    GlobalID ( type: esriFieldTypeGlobalID, alias: GlobalID, length: 38 )
    SHAPE.STArea() ( type: esriFieldTypeDouble, alias: SHAPE.STArea() )
    SHAPE.STLength() ( type: esriFieldTypeDouble, alias: SHAPE.STLength() )
    TaxLandUseCodes ( type: esriFieldTypeString, alias: TaxLandUseCodes, length: 50 , Coded Values: [CA: Canal- An open man-made waterway whose purpose is to carry water from one place to another. Usually refers to main waterways supplying irrigation water to one or more fields or farms.] , [CR: Creeks- Streams and creeks. Annual or seasonal waterways that are natural, not man- made.] , [DW: Drainage Waterway- Partially usable land. Broad and shallow channels usually planted with grass (not crops) and designed to prevent soil erosion while draining runoff water from adjacent cropland.] , ...14 more... )
    USDAcode ( type: esriFieldTypeString, alias: USDAcode, length: 150 , Coded Values: [BL: Building/Facility Land] , [IC: Irrigated Cropland] , [NA: Non-Ag Land] , ...4 more... )
    Div ( type: esriFieldTypeString, alias: Div, length: 50 )
    ParcelID ( type: esriFieldTypeString, alias: ParcelID, length: 50 )
 */
/**
 * Map server configuration for the `BaseUnits/MapServer/0` base layer — source for land use classification data.
 */
export const LandUseMapServer = {
  url: `${azureProxyEsriService}/Base/BaseUnits/MapServer/0`,
};

/**
 * Returns a definition expression for the current (or specified) year.
 * During the first few months of the year the previous year may have more
 * complete data — callers can pass an explicit year override when needed.
 */
export function landUseYearExpression(year?: number): string {
  const y = year ?? new Date().getFullYear();
  return `Year=${y}`;
}

/**
 * A pre-constructed ArcGIS FeatureLayer for the BaseUnits/MapServer/0 base layer —
 * source for land use classification data, scoped to the current year.
 */
export const landUseBaseLayer = new FeatureLayer(
  {
    url: LandUseMapServer.url,
    definitionExpression: landUseYearExpression(),
  }
);
// const meh = () => await getFeatureLayerByLayerName();

/**
 * A pre-constructed ArcGIS GroupLayer for grouping acquisition-related layers on the map, identified by the id 'acquisition-group-layer'.
 */
const acquisitionGroupLayer = new GroupLayer({ id: 'acquisition-group-layer' });

/**
 * Cattle Layers?:
 *
 * DCF feedlot:pens         https://hdqgisweb2.agreserves.com/arcgis/rest/services/DCF/feedlot_FRIGIS/MapServer/14
 * DCF feedlot:pastures     https://hdqgisweb2.agreserves.com/arcgis/rest/services/DCF/feedlot_FRIGIS/MapServer/16
 * DCC feedlot:pastures     https://hdqgisweb2.agreserves.com/arcgis/rest/services/DCC/DCC/MapServer/13
 * SHR pastures:            https://hdqgisweb2.agreserves.com/arcgis/rest/services/SHER/Sheridan/MapServer/15
 * NAV pastures:            https://hdqgisweb2.agreserves.com/arcgis/rest/services/NAV/NavasotaRanch/MapServer/7
 * TCLL pastures:           https://hdqgisweb2.agreserves.com/arcgis/rest/services/TLL/TCLL/MapServer/6
 * TRI pastures:            https://hdqgisweb2.agreserves.com/arcgis/rest/services/TRI/Triangle_FRIGIS/MapServer/25
 * DLL pastures:            https://hdqgisweb2.agreserves.com/arcgis/rest/services/DLL/DLL_FRIGIS/MapServer/49
 * DCT pastures:            https://hdqgisweb2.agreserves.com/arcgis/rest/services/DCT/DCT/MapServer/2
 * REX fields:              https://hdqgisweb2.agreserves.com/arcgis/rest/services/REX/RexRanch_FRIGIS/MapServer/11
 * DRA (Alberta) fields:    https://hdqgisweb2.agreserves.com/arcgis/rest/services/DRA/DRA_FRIGIS/MapServer/16
 * SCC fields:              https://hdqgisweb2.agreserves.com/arcgis/rest/services/SCC/Sooner_FRIGIS/MapServer/10
 * WIN fields:              https://hdqgisweb2.agreserves.com/arcgis/rest/services/WIN/Winnecook/MapServer/9
 */

/**
 * Perm Planting Layers?
 * SVF Blocks:              https://hdqgisweb2.agreserves.com/arcgis/rest/services/California/SVF_FeatureService/MapServer/0
 * SVF Crop & Status:       https://hdqgisweb2.agreserves.com/arcgis/rest/services/California/SVF_FeatureService/MapServer/2
 * DFC Blocks               https://hdqgisweb2.agreserves.com/arcgis/rest/services/California/DFC_FeatureService/MapServer/0
 * DFC Blocks2?             https://hdqgisweb2.agreserves.com/arcgis/rest/services/California/DFC_FeatureService/MapServer/2
 * SHF (georgia) service:   https://hdqgisweb2.agreserves.com/arcgis/rest/services/SHF/Georgia/MapServer
 * SHO (georgia) blocks:    https://hdqgisweb2.agreserves.com/arcgis/rest/services/SHO/SHO_Blocks/MapServer/0
 * DCT timber:              https://hdqgisweb2.agreserves.com/arcgis/rest/services/DCT/DCT/MapServer/5
 *
 */

/**
 * Infrastructure & Buildings:
 * Building Points:         https://hdqgisweb2.agreserves.com/arcgis/rest/services/Buildings/Buildings/MapServer/0
 * CFF Building Footprint:  https://hdqgisweb2.agreserves.com/arcgis/rest/services/CFF/CanyonFallsFarm/MapServer/19
 * TVF Building Footprint:  https://hdqgisweb2.agreserves.com/arcgis/rest/services/TVF/TVF/MapServer/4
 */
/**
 * TODO:
 * MEX,ENG,CHILE,BRAZIL,AGNW, ARG
 */
